import { useEffect, useState } from "react";
import AuthorizedLayout from "@/layouts/AuthorizedLayout.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Patient } from "@/lib/types/models.ts";
import { Plus, Search } from "lucide-react";
import { usePage } from "@inertiajs/react";
import CreatePatientDialog from "@/components/dialogs/CreatePatientDialog.tsx";
import EditPatientDialog from "@/components/dialogs/EditPatientDialog.tsx";
import PaginationControls from "@/components/PaginationControls.tsx";
import PatientsTable from "@/components/patients/PatientsTable.tsx";
import { router } from "@inertiajs/core";
import { PatientPayload, PatientUpdatePayload } from "@/lib/types/payloads.ts";
import {PageTitle} from "@/components/PageTitle.tsx";
import {MobilePatientCard} from "@/components/MobilePatientCard.tsx";

export default function PatientsIndex() {
    const { patients, pagy, translations: t, doctors, orthodontists } = usePage().props as any;

    const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

    const [nameSearch, setNameSearch] = useState("");
    const [citySearch, setCitySearch] = useState("");
    const [doctorSearch, setDoctorSearch] = useState("");
    const [orthodonticsSearch, setOrthodonticsSearch] = useState("");
    const [orthodontistSearch, setOrthodontistSearch] = useState("");
    const [recordSearch, setRecordSearch] = useState("");
    const [unpaidSearch, setUnpaidSearch] = useState("");

    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            setNameSearch(params.get("name") ?? "");
            setCitySearch(params.get("city") ?? "");
            setDoctorSearch(params.get("doctor") ?? "");
            setOrthodonticsSearch(params.get("orthodontics") ?? "");
            setOrthodontistSearch(params.get("orthodontist") ?? "");
            setRecordSearch(params.get("record_number") ?? "");
            setUnpaidSearch(params.get("unpaid") ?? "");
        } catch {}
    }, []);

    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        router.get(
            "/patients",
            {
                page: 1,
                name: nameSearch,
                city: citySearch,
                doctor: doctorSearch,
                orthodontics: orthodonticsSearch,
                orthodontist: orthodontistSearch,
                record_number: recordSearch,
                unpaid: unpaidSearch,
            },
            { preserveState: true, replace: true, onFinish: () => setLoading(false) }
        );
    };

    const handleSortChange = (column: string) => {
        const newDir = sortBy === column ? (sortDir === "asc" ? "desc" : "asc") : "asc";
        setSortBy(column);
        setSortDir(newDir);
        setLoading(true);
        router.get(
            "/patients",
            {
                page: 1,
                name: nameSearch,
                city: citySearch,
                doctor: doctorSearch,
                orthodontics: orthodonticsSearch,
                orthodontist: orthodontistSearch,
                record_number: recordSearch,
                unpaid: unpaidSearch,
                sort_by: column,
                sort_dir: newDir,
            },
            { preserveState: true, replace: true, onFinish: () => setLoading(false) }
        );
    };

    const goToPage = (page: number) => {
        if (!page || page === pagy.page) return;
        setLoading(true);
        router.get(
            "/patients",
            {
                page,
                per_page: 10,
                name: nameSearch,
                city: citySearch,
                doctor: doctorSearch,
                orthodontics: orthodonticsSearch,
                orthodontist: orthodontistSearch,
                record_number: recordSearch,
                unpaid: unpaidSearch,
                sort_by: sortBy,
                sort_dir: sortDir,
            },
            { preserveState: true, replace: true, onFinish: () => setLoading(false) }
        );
    };

    const handleCreate = async (data: PatientPayload): Promise<Patient> => {
        const res = await fetch('/patients', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({ patient: data }),
            credentials: 'same-origin',
        });

        if (!res.ok) throw new Error();

        const json = await res.json();
        return json.patient;
    };

    const handleUpdate = async (id: number, data: PatientUpdatePayload) => {
        router.patch(`/patients/${id}`, { patient: data as Record<string, any> });
        setIsEditDialogOpen(false);
    };

    return (
        <AuthorizedLayout>
                <PageTitle>{t.title}</PageTitle>

                <div className="p-6 space-y-4">
                    <form onSubmit={handleSearch} className="flex flex-col gap-3 w-full">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 w-full">
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder={t.placeholder_name_search}
                                    value={nameSearch}
                                    onChange={(e) => setNameSearch(e.target.value)}
                                    className="pl-8 w-full"
                                />
                            </div>

                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder={t.placeholder_city_search}
                                    value={citySearch}
                                    onChange={(e) => setCitySearch(e.target.value)}
                                    className="pl-8 w-full"
                                />
                            </div>

                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder={t.placeholder_doctor_search ?? "Doktor"}
                                    value={doctorSearch}
                                    onChange={(e) => setDoctorSearch(e.target.value)}
                                    className="pl-8 w-full"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 w-full">
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder="Ortodoncija (da/ne)"
                                    value={orthodonticsSearch}
                                    onChange={(e) => setOrthodonticsSearch(e.target.value)}
                                    className="pl-8 w-full"
                                />
                            </div>

                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder="Ortodont"
                                    value={orthodontistSearch}
                                    onChange={(e) => setOrthodontistSearch(e.target.value)}
                                    className="pl-8 w-full"
                                />
                            </div>

                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder="Broj kartona"
                                    value={recordSearch}
                                    onChange={(e) => setRecordSearch(e.target.value)}
                                    className="pl-8 w-full"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 w-full">
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder="Dužnici (da/ne)"
                                    value={unpaidSearch}
                                    onChange={(e) => setUnpaidSearch(e.target.value)}
                                    className="pl-8 w-full"
                                />
                            </div>

                            <div></div>
                            <div></div>
                        </div>
                        <div className="flex justify-end md:justify-start md:col-span-2 gap-2">
                            <Button type="submit" size="icon">
                                <Search />
                            </Button>
                            <Button size="icon" onClick={() => setIsCreateDialogOpen(true)}>
                                <Plus />
                            </Button>
                        </div>
                    </form>

                    {loading && <Skeleton className="h-10 w-full rounded-md" />}

                    {!loading && patients.length === 0 ? (
                        <p className="text-muted-foreground">{t.no_patients}</p>
                    ) : (
                        <>
                            <div className="hidden md:block">
                                <div className="overflow-x-auto">
                                    <PatientsTable
                                        patients={patients}
                                        translations={t}
                                        onEdit={(patient: Patient) => {
                                            setEditingPatient(patient);
                                            setIsEditDialogOpen(true);
                                        }}
                                        sortBy={sortBy}
                                        sortDir={sortDir}
                                        onSortChange={handleSortChange}
                                        loading={loading}
                                    />
                                </div>
                            </div>

                            <div className="block md:hidden mt-2 space-y-3 overflow-x-hidden">
                                {patients.map((p: any) => (
                                    <MobilePatientCard
                                        key={p.id}
                                        patient={p}
                                        translations={t}
                                        onEdit={(patient) => {
                                            setEditingPatient(patient);
                                            setIsEditDialogOpen(true);
                                        }}
                                    />
                                ))}
                            </div>

                            {patients.length > 0 && <PaginationControls pagy={pagy} onPageChange={goToPage} />}
                        </>
                    )}
                </div>

                <CreatePatientDialog
                    isOpen={isCreateDialogOpen}
                    onOpenChange={setIsCreateDialogOpen}
                    onCreate={handleCreate}
                    doctors={doctors}
                    orthodontists={orthodontists}
                />

                {editingPatient && (
                    <EditPatientDialog
                        isOpen={isEditDialogOpen}
                        onOpenChange={setIsEditDialogOpen}
                        onUpdate={(data) => handleUpdate(editingPatient.id, data)}
                        patient={editingPatient}
                        doctors={doctors}
                        orthodontists={orthodontists}
                    />
                )}
        </AuthorizedLayout>
    );
}